import { useCallback, useMemo } from "react";
import useSWR from "swr";
import axios from "axios";
import moment from "moment-timezone";
import { Card } from "reactstrap";
import Table from "./Table";
import useApi from "../../../../utils/api";
import { useColumns } from "./useColumns";
import { useDateIdMap } from "../../components/TimeSheetTable/hooks/useDateIdMap";
import { useDateTimesheetMap } from "../../components/TimeSheetTable/hooks/useDateTimesheetMap";
import { useColumnsPreferences } from "../../../Settings/Configuration/hooks/useColumnsPreferences";
import { useDateFilters } from "../../hooks/useDateFilters";
import { formatTimeSheetParams } from "../../api/helpers";

function OtherProfileTable({
    user,
    params,
    viewSummary,
    canViewRates,
    view,
    hiddenColumns,
}) {
    const { from, to } = useDateFilters();
    const { authGet } = useApi();
    const swrKey = useMemo(() => {
        if (!user?.id) return null;

        return [
            `/time-sheet/${user.id}`,
            formatTimeSheetParams(params)
        ];
    }, [user?.id, params]);

    const fetcher = useCallback(async (key) => {
        const [url, query] = key;

        const { result } = await authGet(url, {
            params: {
                pagination: false,
                ...query,
            },
        });

        return result;
    }, [authGet]);

    const clockExceptionsRequest = useMemo(() => {
        const url = axios.getUri({
            url: "/exception/me",
            params: {
                startDate: moment(params.from).format("YYYY-MM-DD"),
                endDate: moment(params.to).format("YYYY-MM-DD"),
            }
        });
        return { url };
    }, [params]);

    const scheduledHoursRequest = useMemo(() => {
        const url = axios.getUri({
            url: "/my/schedules/scheduled-hours-by-date",
            params: {
                startDate: moment(params.from).format("YYYY-MM-DD"),
                endDate: moment(params.to).format("YYYY-MM-DD"),
            }
        });
        return { url };
    }, [params]);

    const {
        data: clockExceptions,
        // isLoading: isLoadingClockExceptions,
        // mutate: mutateClockExceptions,
    } = useSWR(clockExceptionsRequest);

    const {
        data: scheduledHours,
        // isLoading: isLoadingScheduledHours,
        // mutate: mutateScheduledHours,
    } = useSWR(scheduledHoursRequest);

    const { data, isLoading } = useSWR(swrKey, fetcher);
    const dateIdMap = useDateIdMap({ data: data || [] });
    const dateTimesheetMap = useDateTimesheetMap({ data: data || [] });

    const columns = useColumns({
        user,
        canEdit: false,
        dateIdMap,
        dateTimesheetMap,
        viewSummary,
        edit: null,
        view,
        remove: null,
        approve: null,
        unapprove: null,
        lock: null,
        unlock: null,
        copy: null,
        scheduleInfo: scheduledHours,
        clockExceptions,
        isMyTimesheet: true,
        waive: null,
        openMissPunchModal: null,
        openInOutDateModal: null,
        canViewRates
    });

    const mergedData = useMemo(() => {
        if (!from || !to) return [];

        const start = moment(from);
        const end = moment(to);

        if (!start.isValid() || !end.isValid()) return [];

        const dateCount = end.diff(start, "day") + 1;
        if (dateCount <= 0) return [];

        const baseDates = Array.from({ length: dateCount }).reduce((acc, _, i) => {
            const date = start.clone().add(i, "day").format("YYYY-MM-DD");
            acc[date] = [];
            return acc;
        }, {});

        const categorisedData = (data || []).reduce((acc, timeSheet) => {
            if (timeSheet?.date && acc[timeSheet.date]) {
                acc[timeSheet.date].push(timeSheet);
            }
            return acc;
        }, baseDates);

        const finalDates = Array.from({ length: dateCount }).reduce((acc, _, i) => {
            const date = start.clone().add(i, "day").format("YYYY-MM-DD");

            if (!acc?.[date]?.length) {
                acc[date] = [{
                    date,
                    id: `${date}_placeholder`,
                    isAdded: true,
                    isPlaceholder: true,
                    isTouched: false,
                    job: undefined,
                    location: undefined,
                    payCode: null,
                    rate: undefined,
                    user
                }];
            }
            return acc;
        }, categorisedData);

        return Object.values(finalDates).flat();
    }, [from, to, data, user]);

    const configColumns = useColumnsPreferences(columns, "myTimesheet");

    return (
        <Card className="timesheet-table mb-0 h-100 position-static">
            <Table
                data={mergedData}
                columns={configColumns}
                loading={isLoading}
                footer={false}
                dateIdMap={dateIdMap}
                hiddenColumns={hiddenColumns}
            />
        </Card>
    );
};

export default OtherProfileTable;
